import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ReportShell } from "@/features/reports/ReportShell";
import { useBranch } from "@/context/BranchContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useLookups } from "@/features/assets/api";
import { isBranchLocked } from "@/lib/roles";
import { Loader2 } from "lucide-react";
import type { ReportFilters } from "@/features/reports/types";
import { useWoSpareConsumptionReport } from "@/features/spares/reportsApi";

export const Route = createFileRoute("/_authenticated/reports/department-spare-usage")({
  component: DepartmentSpareUsageReport,
});

type Group = {
  key: string;
  branch_name: string;
  department_name: string;
  category_name: string;
  spare_name: string;
  spare_code: string;
  net_qty: number;
  net_cost: number;
  wo_ids: Set<string>;
};

function DepartmentSpareUsageReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const { currentBranchId } = useBranch();
  const { data: me } = useCurrentUser();
  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranchId = lockedBranchId ?? filters.branchId ?? currentBranchId ?? null;
  const { data: lookups } = useLookups(effectiveBranchId ?? undefined);

  const { data, isLoading } = useWoSpareConsumptionReport({
    branchId: effectiveBranchId,
    departmentId: filters.departmentId ?? null,
    categoryId: filters.categoryId ?? null,
    dateFrom: filters.dateFrom ?? null,
    dateTo: filters.dateTo ?? null,
  });

  const grouped = useMemo(() => {
    const cats = new Map<string, string>();
    (lookups?.categories ?? []).forEach((c) => cats.set(c.id, c.name));
    const map = new Map<string, Group>();
    (data ?? []).forEach((r) => {
      const net = Number(r.quantity ?? 0) - Number(r.returned_quantity ?? 0);
      if (net <= 0 && (r.quantity ?? 0) === 0) return;
      const rate = Number(r.unit_cost ?? 0);
      const cost = net * rate;
      const firstCat = Array.isArray(r.spare?.compatible_asset_categories) ? r.spare!.compatible_asset_categories![0] : null;
      const catName = firstCat ? (cats.get(firstCat) ?? "—") : "—";
      const key = `${r.branch_id ?? ""}|${r.department_id ?? ""}|${catName}|${r.spare_id ?? r.spare_name}`;
      const existing = map.get(key);
      if (existing) {
        existing.net_qty += net;
        existing.net_cost += cost;
        if (r.wo_id) existing.wo_ids.add(r.wo_id);
      } else {
        map.set(key, {
          key,
          branch_name: r.branch?.name ?? "—",
          department_name: r.department?.name ?? "—",
          category_name: catName,
          spare_name: r.spare?.name ?? r.spare_name,
          spare_code: r.spare?.code ?? "",
          net_qty: net,
          net_cost: cost,
          wo_ids: new Set(r.wo_id ? [r.wo_id] : []),
        });
      }
    });
    return [...map.values()].sort((a, b) => b.net_cost - a.net_cost);
  }, [data, lookups]);

  const exportRows = useMemo(() => () => grouped.map((g) => ({
    Branch: g.branch_name,
    Department: g.department_name,
    "Spare Category": g.category_name,
    "Spare Code": g.spare_code,
    "Spare Name": g.spare_name,
    "Net Used Qty": g.net_qty,
    "Net Cost": +g.net_cost.toFixed(2),
    "Work Order Count": g.wo_ids.size,
  })), [grouped]);

  return (
    <ReportShell
      id="R-39" title="Department-wise Spare Usage"
      description="Spare consumption grouped by branch, department and spare category."
      filters={filters} onChange={setFilters}
      enabled={["branchId", "departmentId", "categoryId", "dateFrom", "dateTo"]}
      exportRows={exportRows}
    >
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Branch</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Spare Category</TableHead>
                <TableHead>Spare</TableHead>
                <TableHead className="text-right">Net Used Qty</TableHead>
                <TableHead className="text-right">Net Cost</TableHead>
                <TableHead className="text-right">WO Count</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {grouped.map((g) => (
                  <TableRow key={g.key}>
                    <TableCell>{g.branch_name}</TableCell>
                    <TableCell>{g.department_name}</TableCell>
                    <TableCell>{g.category_name}</TableCell>
                    <TableCell><div className="font-medium">{g.spare_name}</div><div className="font-mono text-xs text-muted-foreground">{g.spare_code}</div></TableCell>
                    <TableCell className="text-right tabular-nums">{g.net_qty}</TableCell>
                    <TableCell className="text-right tabular-nums">{g.net_cost.toFixed(2)}</TableCell>
                    <TableCell className="text-right tabular-nums">{g.wo_ids.size}</TableCell>
                  </TableRow>
                ))}
                {grouped.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">No spare records found for the selected filters.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
          <div className="border-t p-3 text-xs text-muted-foreground">Showing {grouped.length} department-spare groups.</div>
        </CardContent>
      </Card>
    </ReportShell>
  );
}
